import { AdminPagination as ZhPagination } from "@/views/components/AdminPagination";
import { Button, Card, Space, Typography } from "antd";
import { PageAlert as Alert } from "@/views/components/PageAlert";
import type { FormEvent } from "react";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Link } from "react-router";

import request from "@/api";
import { TRADE_ORDER_STATE } from "@zhihaoscm/meta";
import { DATETIME_PICKER_FORMAT } from "@/config";
import { dateFormat } from "@/utils";
import { getWidthByText } from "@/utils/getWidthByText";
import { confirmDialog } from "@/utils/confirmDialog";
import { notifyOperationSuccess } from "@/utils/operationFeedback";
import { getCollapsedMultiSelectWidth } from "@/utils/select";
import { useUserStore } from "@/views/stores";
import { normalizeOperationColumns } from "@/views/components/ProTable";
import { TableSearchIcon } from "@/views/components/TableSearchIcon";
import AssignDialog, { type assignSubmitData } from "@/views/components/AssignDialog/AssignDialog";
import { loadReceivePaymentSpecials } from "@/api/services/receivePaymentService";
import EditPaymentDialog, { type ReceivePaymentUpdatePayload } from "./tradeOrder/components/dialog/editPaymentDialog";
import type {
    CancelReceivePaymentForm,
    ConfirmReceivePaymentForm,
    File as EntityFile,
    ReceivePayment,
    ReceivePaymentVo,
    ServiceSpecialVo
} from "@zhihaoscm/entity";
import {
    DateRangePicker as ZhDateRangePicker,
    Table as ZhTable,
    Dialog as ZhDialog,
    type TableColumnProps,
    type TableSortChangeContext,
    Button as ZhFilterButton,
    Input as ZhFilterInput,
    Select as ZhFilterSelect
} from "zhihao-ui-react";
import ConfirmPaymentDialog from "./components/payment/confirmPaymentDialog";
import {
    assignTradeCollection,
    cancelTradeCollection,
    confirmTradeCollection,
    deleteTradeCollection,
    loadTradeCollectionDetail,
    loadTradeCollectionVoucherFiles,
    revokeTradeCollection,
    updateTradeCollection,
    uploadTradeCollectionVoucher
} from "./TradeCollectionDetailPage";

export interface TradeCollectionListResult {
    list: ReceivePaymentVo[];
    total: number;
}

export interface TradeCollectionSummary {
    paidAmount: number;
    pendingPaymentAmount: number;
    receivePaymentAmount: number;
}

export interface TradeCollectionQuery {
    page: number;
    size: number;
    id?: string;
    orderId?: string;
    keyword?: string;
    paymentTypes?: number[];
    states?: number[];
    beginTime?: string;
    endTime?: string;
    handlerName?: string;
    sortKey?: string;
    sortOrder?: "asc" | "desc";
}

export interface TradeCollectionPageProps {
    loadCollections?: (query: TradeCollectionQuery) => Promise<TradeCollectionListResult>;
    loadSummary?: () => Promise<TradeCollectionSummary>;
    loadSpecials?: (keyword: string) => Promise<ServiceSpecialVo[]>;
    assignPayment?: (payload: { orderId: number; handlerId: string }) => Promise<unknown>;
    updatePayment?: (id: number | string, payload: Partial<ReceivePayment>) => Promise<unknown>;
    confirmPayment?: (payload: ConfirmReceivePaymentForm) => Promise<unknown>;
    loadPaymentDetail?: (id: number | string) => Promise<ReceivePaymentVo>;
    uploadPaymentFile?: (file: Blob) => Promise<Pick<EntityFile, "id" | "name" | "path">>;
    loadVoucherFiles?: (fileIds: number[]) => Promise<EntityFile[]>;
    cancelPayment?: (payload: CancelReceivePaymentForm) => Promise<unknown>;
    revokePayment?: (payload: CancelReceivePaymentForm) => Promise<unknown>;
    deletePayment?: (id: number | string) => Promise<unknown>;
    permissions?: string[];
    currentUserId?: number;
}

/** 操作列单侧保留的最小内边距，避免链接贴住固定列边界。 */
const TRADE_COLLECTION_OPERATION_PADDING = 12;

/** 操作链接之间沿用旧版 Vue 的 14px 间距。 */
const TRADE_COLLECTION_OPERATION_GAP = 14;

/** 无数据或无法计算动作时的收款操作列兜底宽度。 */
export const TRADE_COLLECTION_OPERATION_WIDTH = 198;

/** 根据当前可见动作计算操作列宽度，避免固定列在动作右侧保留无意义空白。 */
export function getTradeCollectionOperationWidth(
    actionLabels: string[],
    measureText: (text: string) => number = getWidthByText
): number {
    if (!actionLabels.length) return TRADE_COLLECTION_OPERATION_WIDTH;

    const textWidth = measureText(actionLabels.join(""));
    const actionPadding = actionLabels.length * 2;
    const actionGap = Math.max(0, actionLabels.length - 1) * TRADE_COLLECTION_OPERATION_GAP;
    return Math.ceil(textWidth + actionPadding + actionGap + TRADE_COLLECTION_OPERATION_PADDING * 2);
}

/** 根据收款状态和权限返回当前行实际可见的操作文案，供渲染和列宽测量共用。 */
export function getTradeCollectionOperationLabels(
    row: ReceivePaymentVo,
    permissions: string[],
    currentUserId?: number
): string[] {
    const payment = row.receivePayment;
    const state = Number(payment.state);
    const hasPermission = (permission: string) => permissions.includes(permission);
    const isHandler = row.handlerId != null && currentUserId != null && String(row.handlerId) === String(currentUserId);
    const labels: string[] = [];

    if (hasPermission("ROLE_RECEIVABLES_MANAGE")) labels.push(row.handlerId ? "变更指派" : "指派");
    if (state === 1 && isHandler && hasPermission("ROLE_TRADE_ORDER_DEAL")) labels.push("修改");
    if (
        isHandler &&
        (hasPermission("ROLE_RECEIVABLES_DEAL") || hasPermission("ROLE_RECEIVABLES_MANAGE")) &&
        [1, 4].includes(state)
    ) {
        labels.push("确认付款");
    }
    if (
        hasPermission("ROLE_RECEIVABLES_R") &&
        ((payment.customFileId?.length && [3, 4].includes(state)) || (state === 5 && payment.paymentFileId?.length))
    ) {
        labels.push("查看凭证");
    }
    if (
        hasPermission("ROLE_RECEIVABLES_MANAGE") &&
        ((![1, 5].includes(Number(payment.paymentType)) && state === 1) || [4, 5].includes(state))
    ) {
        labels.push(state === 5 ? "撤回" : "取消");
    }
    // 删除资格沿用 Vue：订单已取消/删除，且收款未支付或线下已支付。
    if (
        hasPermission("ROLE_RECEIVABLES_MANAGE") &&
        Boolean(row.orderDel || row.orderState === TRADE_ORDER_STATE.CANCELLED.code) &&
        [3, 5].includes(state)
    ) {
        labels.push("删除");
    }

    return labels;
}

/**
 * 按旧版 Element Plus 表格的规则分配收款列表的可伸缩列。
 * 固定列保持原始宽度，采购用户/费用类型/费用说明按最小宽度比例共同吸收宽屏剩余空间。
 */
export function getTradeCollectionColumnWidths(containerWidth?: number) {
    const flexibleBaseWidths = [250, 92, 160];
    const fixedWidth = 1439;
    const flexibleBaseTotal = flexibleBaseWidths.reduce((total, width) => total + width, 0);
    const baseTableWidth = fixedWidth + flexibleBaseTotal;
    if (!containerWidth || containerWidth <= 0 || containerWidth <= baseTableWidth) {
        return flexibleBaseWidths;
    }

    const scale = (containerWidth - fixedWidth) / flexibleBaseTotal;
    const customerWidth = Math.round(flexibleBaseWidths[0] * scale);
    const paymentTypeWidth = Math.round(flexibleBaseWidths[1] * scale);
    // 最后一列吸收取整误差，确保所有列宽之和与容器宽度一致，避免 Ant Table 再次整体拉伸。
    const remarkWidth = Math.max(0, Math.round(containerWidth - fixedWidth - customerWidth - paymentTypeWidth));
    return [customerWidth, paymentTypeWidth, remarkWidth];
}

const PAYMENT_TYPE_LABELS: Record<number, string> = {
    1: "订单定金",
    3: "发航货款",
    4: "开仓货款",
    5: "订单货款"
};

const RECEIVE_STATE_LABELS: Record<number, string> = {
    1: "待支付",
    2: "已支付 (线上)",
    3: "未支付",
    4: "待确认支付",
    5: "已支付 (线下)"
};

const PAYMENT_TYPE_OPTIONS = [
    { label: "订单货款", value: "5" },
    { label: "订单定金", value: "1" },
    { label: "发航货款", value: "3" },
    { label: "开仓货款", value: "4" }
];

const RECEIVE_STATE_OPTIONS = [
    { label: "待支付", value: "1" },
    { label: "待确认支付", value: "4" },
    { label: "已支付 (线上)", value: "2" },
    { label: "已支付 (线下)", value: "5" },
    { label: "未支付", value: "3" }
];

/** 将 Vue 页面使用的金额字段格式化为两位小数，保持管理后台金额可读性。 */
function formatMoney(value: number | undefined): string {
    return (Number(value) || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/** 将日期输入转换为 Vue 接口要求的当天起止时间。 */
function formatDateTime(value: string, endOfDay = false): string {
    return `${value}T${endOfDay ? "23:59:59" : "00:00:00"}`;
}

/** 沿用 Vue 的收款分页接口和请求路径，只保留前端列表需要的查询字段。 */
export async function loadTradeCollections(query: TradeCollectionQuery): Promise<TradeCollectionListResult> {
    const response = await request.service.get<TradeCollectionListResult>("/admin/receive-payment/paging", {
        params: query
    });
    return response.data;
}

/** 沿用 Vue 的收款金额统计接口及三个金额字段。 */
export async function loadTradeCollectionSummary(): Promise<TradeCollectionSummary> {
    const response = await request.service.get<TradeCollectionSummary>("/admin/receive-payment/amount");
    return response.data;
}

/** 将客户企业数据映射为 Vue 收款列表的采购用户文案。 */
function getCustomerLabel(row: ReceivePaymentVo): string {
    const customer = row.receivePayment.customerEnterprise;
    const name = customer?.name || customer?.realName || "";
    return `${name}(${customer?.mobile || ""})`;
}

/** 交易管理收款列表首屏，复用 Vue 的分页、统计和字段契约。 */
export function TradeCollectionPage({
    loadCollections = loadTradeCollections,
    loadSummary = loadTradeCollectionSummary,
    loadSpecials = loadReceivePaymentSpecials,
    assignPayment = assignTradeCollection,
    updatePayment = updateTradeCollection,
    confirmPayment = confirmTradeCollection,
    loadPaymentDetail = loadTradeCollectionDetail,
    uploadPaymentFile = uploadTradeCollectionVoucher,
    loadVoucherFiles = loadTradeCollectionVoucherFiles,
    cancelPayment = cancelTradeCollection,
    revokePayment = revokeTradeCollection,
    deletePayment = deleteTradeCollection,
    permissions,
    currentUserId
}: TradeCollectionPageProps) {
    const tableShellRef = useRef<HTMLDivElement>(null);
    const [tableContainerWidth, setTableContainerWidth] = useState<number>();
    const storePermissions = useUserStore(state => state.userInfo.permissions || []);
    const storeUserId = useUserStore(state => state.userInfo.user?.id);
    // 路由直接挂载页面时从登录态补齐 Vue 页面依赖的权限和当前专员身份；测试/嵌入场景仍可显式覆盖。
    const effectivePermissions = permissions ?? storePermissions;
    const effectiveCurrentUserId = currentUserId ?? (storeUserId == null ? undefined : Number(storeUserId));
    const [assignVisible, setAssignVisible] = useState(false);
    const [editVisible, setEditVisible] = useState(false);
    const [confirmVisible, setConfirmVisible] = useState(false);
    const [specialists, setSpecialists] = useState<ServiceSpecialVo[]>([]);
    const [selectedHandlerId, setSelectedHandlerId] = useState<number>();
    const [assignLoading, setAssignLoading] = useState(false);
    const [assignError, setAssignError] = useState<string | null>(null);
    const [currentRow, setCurrentRow] = useState<ReceivePaymentVo | null>(null);
    const [draftFilters, setDraftFilters] = useState({
        id: "",
        orderId: "",
        keyword: "",
        paymentTypes: [] as string[],
        states: [] as string[],
        beginDate: "",
        endDate: "",
        handlerName: ""
    });
    const [query, setQuery] = useState<TradeCollectionQuery>({ page: 1, size: 10 });
    const [items, setItems] = useState<ReceivePaymentVo[]>([]);
    const [total, setTotal] = useState(0);
    const [summary, setSummary] = useState<TradeCollectionSummary>({
        paidAmount: 0,
        pendingPaymentAmount: 0,
        receivePaymentAmount: 0
    });
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [voucherPreviewOpen, setVoucherPreviewOpen] = useState(false);
    const [voucherFiles, setVoucherFiles] = useState<EntityFile[]>([]);

    useLayoutEffect(() => {
        const element = tableShellRef.current;
        if (!element) return;

        const updateWidth = () => {
            const width = element.clientWidth;
            if (width > 0) setTableContainerWidth(width);
        };
        updateWidth();
        if (typeof ResizeObserver === "undefined") return;
        const observer = new ResizeObserver(updateWidth);
        observer.observe(element);
        return () => observer.disconnect();
    }, []);

    /** 将 zhihao Table 排序方向转换为 Vue 的 sortKey/sortOrder 请求字段。 */
    const handleSortChange = (sort: TableSortChangeContext<ReceivePaymentVo>) => {
        const sortKeyMap: Record<string, string> = {
            payTime: "pay_time",
            createdTime: "created_time"
        };
        const columnKey = sort.prop || "";
        const sortKey = columnKey ? sortKeyMap[columnKey] : undefined;
        const sortOrder = sort.order === "ascending" ? "asc" : sort.order === "descending" ? "desc" : undefined;
        setQuery(current => ({
            ...current,
            page: 1,
            ...(sortKey && sortOrder ? { sortKey, sortOrder } : {})
        }));
    };

    const hasPermission = (permission: string) => effectivePermissions.includes(permission);

    /** 写操作完成后重新读取当前筛选条件，保持 Vue 收款列表的刷新语义。 */
    const reloadCollections = async () => {
        const result = await loadCollections(query);
        setItems(result.list || []);
        setTotal(Number(result.total) || 0);
    };

    /** 记录当前列表操作行，供 Vue 风格的指派和确认弹窗复用。 */
    const selectRow = (row: ReceivePaymentVo) => {
        setCurrentRow(row);
        setErrorMessage(null);
    };

    /** 打开 Vue 通用指派弹框，并按订单接口加载可指派专员及当前专员回显。 */
    const openAssign = async (row: ReceivePaymentVo) => {
        selectRow(row);
        setSelectedHandlerId(row.handlerId == null ? undefined : Number(row.handlerId));
        setSpecialists([]);
        setAssignError(null);
        setAssignVisible(true);
        setAssignLoading(true);
        try {
            setSpecialists((await loadSpecials("")) || []);
        } catch {
            setAssignError("订单专员查询失败");
        } finally {
            setAssignLoading(false);
        }
    };

    /** 支持 Vue 弹框中的姓名搜索，同时把搜索结果留在受控候选列表中。 */
    const searchAssignSpecialists = async (keyword: string) => {
        setAssignLoading(true);
        try {
            const result = (await loadSpecials(keyword)) || [];
            setSpecialists(result);
            setAssignError(null);
            return result;
        } catch {
            setSpecialists([]);
            setAssignError("订单专员查询失败");
            return [];
        } finally {
            setAssignLoading(false);
        }
    };

    /** 提交 Vue 通用指派弹框的 personId，保持订单指派接口参数契约。 */
    const submitAssign = async ({ officerId }: assignSubmitData) => {
        if (!currentRow || officerId == null) {
            setAssignError("请选择订单专员");
            throw new Error("请选择订单专员");
        }
        try {
            await assignPayment({
                orderId: Number(currentRow.receivePayment.orderId),
                handlerId: String(officerId)
            });
            notifyOperationSuccess("指派成功");
            await reloadCollections();
            setAssignVisible(false);
        } catch (error) {
            setAssignError("收款指派失败");
            throw error;
        }
    };

    /** 提交共享确认付款弹窗，并在成功后刷新列表。 */
    const submitConfirm = async (payload: ConfirmReceivePaymentForm) => {
        try {
            await confirmPayment(payload);
            notifyOperationSuccess("已确认");
            setConfirmVisible(false);
            await reloadCollections();
        } catch {
            setErrorMessage("确认付款失败");
        }
    };

    /** 打开确认付款前刷新所选收款单，避免使用列表中的过期 version。 */
    const openConfirm = async (row: ReceivePaymentVo) => {
        selectRow(row);
        try {
            const latest = await loadPaymentDetail(row.receivePayment.id);
            setCurrentRow(latest);
            setConfirmVisible(true);
        } catch {
            setErrorMessage("收款详情加载失败");
        }
    };

    /** 提交旧 Vue 编辑弹窗的完整业务字段，避免后端因缺少金额和关联字段而报错。 */
    const editPayment = async (payload: ReceivePaymentUpdatePayload) => {
        if (!currentRow) return;
        try {
            await updatePayment(currentRow.receivePayment.id, payload);
            notifyOperationSuccess("已修改");
            setEditVisible(false);
            await reloadCollections();
        } catch {
            setErrorMessage("收款修改失败");
        }
    };

    /** 加载收款单的全部凭证，并在列表弹窗中展示文件信息。 */
    const viewVoucher = async (row: ReceivePaymentVo) => {
        const fileIds = row.receivePayment.paymentFileId || row.receivePayment.customFileId || [];
        try {
            const files = await loadVoucherFiles(fileIds);
            setVoucherFiles(files);
            setVoucherPreviewOpen(true);
        } catch {
            setErrorMessage("收款凭证加载失败");
        }
    };

    // 与旧 Vue 凭证列表保持一致，文件名可单独打开原文件，弹窗完整展示多个凭证。
    const voucherPreviewColumns: TableColumnProps<EntityFile>[] = [
        {
            key: "name",
            label: "文件名称",
            prop: "name",
            render: ({ row }) => (
                <a href={row.path} rel="noreferrer" target="_blank">
                    {row.name || "-"}
                </a>
            )
        },
        { key: "type", label: "文件格式", width: 160, prop: "type", render: ({ row }) => row.type || "-" },
        {
            key: "createdTime",
            label: "上传时间",
            width: 160,
            prop: "createdTime",
            render: ({ row }) => (row.createdTime ? dateFormat(row.createdTime, DATETIME_PICKER_FORMAT) : "-")
        }
    ];

    /** 根据支付状态选择 Vue 取消或撤回接口。 */
    const cancelOrRevoke = async (row: ReceivePaymentVo) => {
        if (!(await confirmDialog(Number(row.receivePayment.state) === 5 ? "是否撤回收款？" : "是否取消收款？")))
            return;
        const payload = { id: row.receivePayment.id, reason: "" };
        try {
            if (Number(row.receivePayment.state) === 5) {
                await revokePayment(payload);
                notifyOperationSuccess("已撤回");
            } else {
                await cancelPayment(payload);
                notifyOperationSuccess("已取消");
            }
            await reloadCollections();
        } catch {
            setErrorMessage("收款取消失败");
        }
    };

    /** 删除关联已取消订单的收款记录。 */
    const removePayment = async (row: ReceivePaymentVo) => {
        if (!(await confirmDialog("是否删除收款记录？"))) return;
        try {
            await deletePayment(row.receivePayment.id);
            notifyOperationSuccess("已删除");
            await reloadCollections();
        } catch {
            setErrorMessage("收款删除失败");
        }
    };

    const updateFilter = (key: keyof typeof draftFilters, value: string | string[]) => {
        setDraftFilters(current => ({ ...current, [key]: value }));
    };

    /** 清空收款列表筛选项，并恢复旧 Vue ProTable 的默认第一页。 */
    const resetFilters = () => {
        setDraftFilters({
            id: "",
            orderId: "",
            keyword: "",
            paymentTypes: [],
            states: [],
            beginDate: "",
            endDate: "",
            handlerName: ""
        });
        setErrorMessage(null);
        setQuery({ page: 1, size: 10 });
    };

    const submitFilters = (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        const beginDate = draftFilters.beginDate.trim();
        const endDate = draftFilters.endDate.trim();

        // 日期范围必须成对提交，避免向 Vue 接口发送不完整的时间边界。
        if (Boolean(beginDate) !== Boolean(endDate)) {
            setErrorMessage("请选择完整创建日期范围");
            return;
        }
        if (beginDate && endDate && beginDate > endDate) {
            setErrorMessage("创建开始日期不能晚于结束日期");
            return;
        }

        const nextQuery: TradeCollectionQuery = {
            page: 1,
            size: query.size
        };
        const textFilters: Array<
            [keyof Pick<TradeCollectionQuery, "id" | "orderId" | "keyword" | "handlerName">, string]
        > = [
            ["id", draftFilters.id],
            ["orderId", draftFilters.orderId],
            ["keyword", draftFilters.keyword],
            ["handlerName", draftFilters.handlerName]
        ];
        for (const [key, value] of textFilters) {
            if (value.trim()) nextQuery[key] = value.trim();
        }
        if (draftFilters.paymentTypes.length) nextQuery.paymentTypes = draftFilters.paymentTypes.map(Number);
        if (draftFilters.states.length) nextQuery.states = draftFilters.states.map(Number);
        if (beginDate && endDate) {
            nextQuery.beginTime = formatDateTime(beginDate);
            nextQuery.endTime = formatDateTime(endDate, true);
        }
        setErrorMessage(null);
        setQuery(nextQuery);
    };

    useEffect(() => {
        let disposed = false;
        void loadCollections(query)
            .then(result => {
                if (!disposed) {
                    setItems(result.list || []);
                    setTotal(Number(result.total) || 0);
                }
            })
            .catch(() => {
                if (!disposed) {
                    setItems([]);
                    setTotal(0);
                    setErrorMessage("收款列表加载失败");
                }
            });
        return () => {
            disposed = true;
        };
    }, [loadCollections, query]);

    useEffect(() => {
        let disposed = false;
        void loadSummary()
            .then(result => {
                if (!disposed) {
                    setSummary({
                        paidAmount: Number(result.paidAmount) || 0,
                        pendingPaymentAmount: Number(result.pendingPaymentAmount) || 0,
                        receivePaymentAmount: Number(result.receivePaymentAmount) || 0
                    });
                }
            })
            .catch(() => {
                if (!disposed) setErrorMessage("收款统计加载失败");
            });
        return () => {
            disposed = true;
        };
    }, [loadSummary]);

    const baseColumns: TableColumnProps<ReceivePaymentVo>[] = [
        {
            label: "收款编号",
            width: 170,
            prop: "id",
            render: ({ row }) => (
                <Link to={`/tradeManage/collection/detail?id=${row.receivePayment.id}`}>{row.receivePayment.id}</Link>
            )
        },
        {
            label: "关联订单",
            width: 170,
            prop: "orderId",
            render: ({ row }) => (
                <Link to={`/tradeManage/tradeOrder/detail?id=${row.receivePayment.orderId}`}>
                    {row.receivePayment.orderId}
                </Link>
            )
        },
        { label: "采购用户", minWidth: 250, prop: "customer", render: ({ row }) => getCustomerLabel(row) },
        {
            label: "费用类型",
            minWidth: 92,
            prop: "paymentType",
            render: ({ row }) => PAYMENT_TYPE_LABELS[Number(row.receivePayment.paymentType)] || "--"
        },
        { label: "费用说明", minWidth: 160, prop: "remark", render: ({ row }) => row.receivePayment.remark || "--" },
        {
            label: "支付金额(元)",
            width: 120,
            prop: "amount",
            align: "right" as const,
            render: ({ row }) => formatMoney(row.receivePayment.amount)
        },
        {
            label: "付款说明",
            width: 200,
            prop: "paymentInstruction",
            render: ({ row }) => row.receivePayment.paymentInstruction || "--"
        },
        {
            label: "支付时间",
            width: 170,
            prop: "payTime",
            sortable: "custom",
            render: ({ row }) =>
                row.receivePayment.payTime ? dateFormat(row.receivePayment.payTime, DATETIME_PICKER_FORMAT) : "--"
        },
        {
            label: "创建时间",
            width: 170,
            prop: "createdTime",
            sortable: "custom",
            render: ({ row }) =>
                row.receivePayment.createdTime
                    ? dateFormat(row.receivePayment.createdTime, DATETIME_PICKER_FORMAT)
                    : "--"
        },
        {
            label: "支付状态",
            width: 120,
            prop: "state",
            render: ({ row }) => RECEIVE_STATE_LABELS[Number(row.receivePayment.state)] || "-"
        },
        { label: "订单专员", width: 105, prop: "handlerName", render: ({ row }) => row.handlerName || "--" },
        {
            label: "操作",
            fixed: "right",
            width: 240,
            className: "trade-collection-operation-column",
            prop: "operation",
            render: ({ row }) => {
                const operationLabels = getTradeCollectionOperationLabels(
                    row,
                    effectivePermissions,
                    effectiveCurrentUserId
                );
                const payment = row.receivePayment;
                const state = Number(payment.state);
                const showEdit = operationLabels.includes("修改");
                const showConfirm = operationLabels.includes("确认付款");
                const showVoucher = operationLabels.includes("查看凭证");
                const showCancel = operationLabels.includes("取消") || operationLabels.includes("撤回");
                const showDelete = operationLabels.includes("删除");

                // 收款列表沿用旧 Vue 的完整操作顺序，确认付款和取消不能被折叠到“更多”菜单中。
                return (
                    <Space>
                        {hasPermission("ROLE_RECEIVABLES_MANAGE") ? (
                            <Button
                                aria-label={row.handlerId ? "变更指派" : "指派"}
                                onClick={() => {
                                    void openAssign(row);
                                }}
                            >
                                {row.handlerId ? "变更指派" : "指派"}
                            </Button>
                        ) : null}
                        {showEdit ? (
                            <Button
                                aria-label="修改"
                                onClick={() => {
                                    selectRow(row);
                                    setEditVisible(true);
                                }}
                            >
                                修改
                            </Button>
                        ) : null}
                        {showConfirm ? (
                            <Button
                                aria-label="确认付款"
                                onClick={() => {
                                    void openConfirm(row);
                                }}
                            >
                                确认付款
                            </Button>
                        ) : null}
                        {showVoucher ? (
                            <Button aria-label="查看凭证" onClick={() => void viewVoucher(row)}>
                                查看凭证
                            </Button>
                        ) : null}
                        {showCancel ? (
                            <Button aria-label={state === 5 ? "撤回" : "取消"} onClick={() => void cancelOrRevoke(row)}>
                                {state === 5 ? "撤回" : "取消"}
                            </Button>
                        ) : null}
                        {showDelete ? (
                            <Button aria-label="删除" onClick={() => void removePayment(row)}>
                                删除
                            </Button>
                        ) : null}
                    </Space>
                );
            }
        }
    ];

    const [customerWidth, paymentTypeWidth, remarkWidth] = getTradeCollectionColumnWidths(tableContainerWidth);
    // 以当前页面所有行的最大可见动作宽度作为固定列宽，避免单行操作较少时右侧留下大块空白。
    const operationWidth = items.length
        ? Math.max(
              ...items.map(row =>
                  getTradeCollectionOperationWidth(
                      getTradeCollectionOperationLabels(row, effectivePermissions, effectiveCurrentUserId)
                  )
              )
          )
        : TRADE_COLLECTION_OPERATION_WIDTH;
    const columns = baseColumns.map(column => {
        if (column.label === "采购用户") return { ...column, width: customerWidth, minWidth: undefined };
        if (column.label === "费用类型") return { ...column, width: paymentTypeWidth, minWidth: undefined };
        if (column.label === "费用说明") return { ...column, width: remarkWidth, minWidth: undefined };
        if (column.label === "操作" && tableContainerWidth) {
            return { ...column, width: operationWidth };
        }
        return column;
    });

    return (
        <section aria-label="trade-collection-page" className="admin-trade-list-page flex h-full min-h-full flex-col">
            <Card className="flex h-full flex-1 flex-col [&_.ant-card-body]:flex [&_.ant-card-body]:min-h-0 [&_.ant-card-body]:flex-1 [&_.ant-card-body]:flex-col">
                <div ref={tableShellRef} className="trade-collection-table-shell">
                    <ZhTable
                        columns={columns}
                        data={items}
                        onSortChange={handleSortChange}
                        pagination={false}
                        rowKey={row => row.receivePayment.id}
                        size="small"
                        isShowResetBtn={false}
                        isShowSearchBtn={false}
                        tableHeader={
                            <div className="admin-table-composed-header">
                                <Typography.Title className="mb-4 mt-0" level={4}>
                                    收款管理
                                </Typography.Title>

                                {errorMessage ? (
                                    <Alert className="mb-4" message={errorMessage} role="alert" type="error" />
                                ) : null}

                                <Space aria-label="收款金额统计" className="admin-trade-stat-cards" size={0}>
                                    <Card className="admin-trade-stat-card" size="small">
                                        <Typography.Text className="admin-trade-stat-card__label">
                                            收款总金额（元）
                                        </Typography.Text>
                                        <Typography.Title className="admin-trade-stat-card__value" level={3}>
                                            {formatMoney(summary.receivePaymentAmount)}
                                        </Typography.Title>
                                    </Card>
                                    <Card className="admin-trade-stat-card" size="small">
                                        <Typography.Text className="admin-trade-stat-card__label">
                                            已支付总金额（元）
                                        </Typography.Text>
                                        <Typography.Title className="admin-trade-stat-card__value" level={3}>
                                            {formatMoney(summary.paidAmount)}
                                        </Typography.Title>
                                    </Card>
                                    <Card className="admin-trade-stat-card" size="small">
                                        <Typography.Text className="admin-trade-stat-card__label">
                                            待支付总金额（元）
                                        </Typography.Text>
                                        <Typography.Title
                                            className="admin-trade-stat-card__value admin-trade-stat-card__value--danger"
                                            level={3}
                                        >
                                            {formatMoney(summary.pendingPaymentAmount)}
                                        </Typography.Title>
                                    </Card>
                                </Space>

                                <form className="admin-table-filter-form mb-4" onSubmit={submitFilters}>
                                    <label className="trade-collection-filter-id">
                                        收款编号
                                        <ZhFilterInput
                                            aria-label="收款编号"
                                            allowClear
                                            placeholder="请输入收款编号搜索"
                                            value={draftFilters.id}
                                            onChange={event => updateFilter("id", event.target.value)}
                                        />
                                    </label>
                                    <label className="trade-collection-filter-order">
                                        关联订单
                                        <ZhFilterInput
                                            aria-label="关联订单"
                                            allowClear
                                            placeholder="请输入订单编号搜索"
                                            value={draftFilters.orderId}
                                            onChange={event => updateFilter("orderId", event.target.value)}
                                        />
                                    </label>
                                    <label className="trade-collection-filter-customer">
                                        采购用户
                                        <ZhFilterInput
                                            aria-label="采购用户"
                                            allowClear
                                            placeholder="请输入采购商账号信息搜索"
                                            value={draftFilters.keyword}
                                            onChange={event => updateFilter("keyword", event.target.value)}
                                        />
                                    </label>
                                    <label className="trade-collection-filter-date">
                                        创建日期
                                        <ZhDateRangePicker
                                            type="daterange"
                                            format="YYYY-MM-DD"
                                            valueFormat="YYYY-MM-DD"
                                            rangeSeparator="-"
                                            startPlaceholder="创建开始日期"
                                            endPlaceholder="创建结束日期"
                                            value={[draftFilters.beginDate || null, draftFilters.endDate || null]}
                                            onChange={(_value, formattedValue) => {
                                                updateFilter("beginDate", formattedValue?.[0] || "");
                                                updateFilter("endDate", formattedValue?.[1] || "");
                                            }}
                                        />
                                    </label>
                                    <label className="trade-collection-filter-payment-type">
                                        费用类型
                                        <ZhFilterSelect
                                            aria-label="费用类型"
                                            allowClear
                                            maxTagCount={1}
                                            mode="multiple"
                                            options={PAYMENT_TYPE_OPTIONS}
                                            placeholder="请选择费用类型"
                                            style={{
                                                width: getCollapsedMultiSelectWidth(
                                                    draftFilters.paymentTypes,
                                                    PAYMENT_TYPE_OPTIONS,
                                                    "请选择费用类型",
                                                    134
                                                )
                                            }}
                                            value={draftFilters.paymentTypes}
                                            onChange={value => updateFilter("paymentTypes", value)}
                                        />
                                    </label>
                                    <label className="trade-collection-filter-state">
                                        支付状态
                                        <ZhFilterSelect
                                            aria-label="支付状态"
                                            allowClear
                                            maxTagCount={1}
                                            mode="multiple"
                                            options={RECEIVE_STATE_OPTIONS}
                                            placeholder="请选择支付状态"
                                            style={{
                                                width: getCollapsedMultiSelectWidth(
                                                    draftFilters.states,
                                                    RECEIVE_STATE_OPTIONS,
                                                    "请选择支付状态",
                                                    110
                                                )
                                            }}
                                            value={draftFilters.states}
                                            onChange={value => updateFilter("states", value)}
                                        />
                                    </label>
                                    <label className="trade-collection-filter-handler">
                                        订单专员
                                        <ZhFilterInput
                                            aria-label="订单专员"
                                            allowClear
                                            placeholder="请输入订单专员搜索"
                                            value={draftFilters.handlerName}
                                            onChange={event => updateFilter("handlerName", event.target.value)}
                                        />
                                    </label>
                                    <ZhFilterButton
                                        aria-label="查询收款"
                                        htmlType="submit"
                                        icon={<TableSearchIcon name="search" />}
                                        type="primary"
                                    >
                                        搜索
                                    </ZhFilterButton>
                                    <ZhFilterButton
                                        aria-label="重置收款"
                                        className="table-filter-reset-button"
                                        icon={<TableSearchIcon name="reset" />}
                                        onClick={resetFilters}
                                    />
                                </form>
                            </div>
                        }
                        append={
                            <div className="admin-table-composed-pagination">
                                <ZhPagination
                                    className="mt-4 self-end"
                                    current={query.page}
                                    pageSize={query.size}
                                    // 对齐旧 Vue ProTable 的分页选项，避免 30/40 条档位无法选择。
                                    pageSizeOptions={[10, 20, 30, 40, 50, 100]}
                                    showSizeChanger
                                    total={total}
                                    onChange={(page, size) => setQuery(current => ({ ...current, page, size }))}
                                />
                            </div>
                        }
                    />
                </div>
                <AssignDialog
                    open={assignVisible}
                    title="指派专员"
                    info={currentRow || undefined}
                    officers={specialists}
                    searchSpecial={searchAssignSpecialists}
                    tips="跟进的订单数量"
                    countFiled="orderCount"
                    curOfficerId={selectedHandlerId}
                    confirmText="确定"
                    loading={assignLoading}
                    error={assignError}
                    onClose={() => {
                        setAssignVisible(false);
                        setCurrentRow(null);
                        setAssignError(null);
                    }}
                    onCurOfficerIdChange={setSelectedHandlerId}
                    onSubmit={submitAssign}
                />
                <EditPaymentDialog
                    data={currentRow?.receivePayment}
                    onCancel={() => setEditVisible(false)}
                    onSubmit={editPayment}
                    open={editVisible}
                />
                <ConfirmPaymentDialog
                    data={currentRow?.receivePayment}
                    onCancel={() => setConfirmVisible(false)}
                    onSubmit={submitConfirm}
                    open={confirmVisible}
                    uploadFile={uploadPaymentFile}
                />
                <ZhDialog
                    footer={null}
                    open={voucherPreviewOpen}
                    title="查看凭证"
                    width={800}
                    onCancel={() => setVoucherPreviewOpen(false)}
                >
                    <ZhTable<EntityFile>
                        columns={normalizeOperationColumns(voucherPreviewColumns, voucherFiles)}
                        data={voucherFiles}
                        pagination={false}
                        rowKey={file => String(file.id)}
                    />
                </ZhDialog>
            </Card>
        </section>
    );
}
